/**
 * Google Analytics Service
 * Provides utility functions for tracking custom events across the application
 */

/**
 * Track a custom event in Google Analytics
 * @param {string} eventName - Name of the event (e.g., 'shipment_created', 'container_viewed')
 * @param {object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, {
      ...eventParams,
      timestamp: new Date()?.toISOString()
    });
  }
};

/**
 * Track shipment-related events
 */
export const trackShipmentEvent = (action, shipmentData = {}) => {
  trackEvent('shipment_action', {
    action_type: action,
    shipment_id: shipmentData?.id,
    shipment_reference: shipmentData?.reference,
    shipment_status: shipmentData?.status,
    page: window.location?.pathname
  });
};

/**
 * Track container-related events
 */
export const trackContainerEvent = (action, containerData = {}) => {
  trackEvent('container_action', {
    action_type: action,
    container_id: containerData?.id,
    container_reference: containerData?.reference,
    container_status: containerData?.status,
    page: window.location?.pathname
  });
};

/**
 * Track user navigation events
 */
export const trackNavigation = (destination, source = null) => {
  trackEvent('navigation', {
    destination,
    source: source || window.location?.pathname,
    user_type: getUserType()
  });
};

/**
 * Track search/filter events
 */
export const trackSearch = (searchType, query, resultsCount = null) => {
  trackEvent('search', {
    search_type: searchType,
    search_query: query,
    results_count: resultsCount,
    page: window.location?.pathname
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (formName, formData = {}) => {
  trackEvent('form_submit', {
    form_name: formName,
    form_type: formData?.type,
    page: window.location?.pathname
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName, buttonContext = {}) => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_context: buttonContext?.context,
    page: window.location?.pathname
  });
};

/**
 * Track file uploads
 */
export const trackFileUpload = (fileType, fileName, fileSize = null) => {
  trackEvent('file_upload', {
    file_type: fileType,
    file_name: fileName,
    file_size: fileSize,
    page: window.location?.pathname
  });
};

/**
 * Track authentication events
 */
export const trackAuth = (action, userType = null) => {
  trackEvent('auth_action', {
    action_type: action,
    user_type: userType,
    page: window.location?.pathname
  });
};

/**
 * Track delivery task events
 */
export const trackDeliveryTask = (action, taskData = {}) => {
  trackEvent('delivery_task', {
    action_type: action,
    task_id: taskData?.id,
    task_status: taskData?.status,
    delivery_zone: taskData?.delivery_zone,
    page: window.location?.pathname
  });
};

/**
 * Track WhatsApp notification events
 */
export const trackWhatsAppNotification = (action, notificationData = {}) => {
  trackEvent('whatsapp_notification', {
    action_type: action,
    notification_type: notificationData?.type,
    recipient_type: notificationData?.recipient_type,
    page: window.location?.pathname
  });
};

/**
 * Track payment events
 */
export const trackPayment = (action, paymentData = {}) => {
  trackEvent('payment_action', {
    action_type: action,
    payment_method: paymentData?.method,
    amount: paymentData?.amount,
    currency: paymentData?.currency || 'XOF',
    page: window.location?.pathname
  });
};

/**
 * Track error events
 */
export const trackError = (errorType, errorMessage, errorContext = {}) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
    error_context: JSON.stringify(errorContext),
    page: window.location?.pathname
  });
};

/**
 * Helper function to determine user type from current path
 */
const getUserType = () => {
  const path = window.location?.pathname;
  if (path?.includes('/admin')) return 'admin';
  if (path?.includes('/driver')) return 'driver';
  if (path?.includes('/client')) return 'client';
  if (path?.includes('/agent-abidjan') || path?.includes('/dashboard-agent-abidjan')) return 'agent_abidjan';
  return 'public';
};

export default {
  trackEvent,
  trackShipmentEvent,
  trackContainerEvent,
  trackNavigation,
  trackSearch,
  trackFormSubmit,
  trackButtonClick,
  trackFileUpload,
  trackAuth,
  trackDeliveryTask,
  trackWhatsAppNotification,
  trackPayment,
  trackError
};
